import React, { useCallback, useEffect, useState } from "react";
import { Newspaper, ExternalLink, MessageSquarePlus, RefreshCw, AlertCircle, Check, Loader2, RotateCcw } from "lucide-react";
import { AuthExpiredError, TurfTalk } from "@/api/client";
import { Skeleton } from "@/components/ui-kit";
import { statusLabel, publishedLabel } from "@/lib/news";
import { loadGameNews, modeLabel, modeSubnote } from "@/lib/gameNews";

const STATUS_STYLE = {
  live: "text-[#4ea152] border-[#4ea152]/40",
  cached: "text-[#F4B740] border-[#F4B740]/40",
  unavailable: "text-white/40 border-white/15",
};

// Prominent, compact news strip for the game page — placed directly under the
// scoreboard. Matchup-first, league-fallback (clearly labelled), 3–5 attributed
// headlines with source/time/link and a "Debate this" action that opens the
// shared game room with the story (dedupe/1-POST handled downstream).
export function GameNewsStrip({ gameId, league = null, awayAbbr = null, homeAbbr = null, onDebate, storyStatus }) {
  const [feed, setFeed] = useState(null);
  const [state, setState] = useState("loading");

  const load = useCallback(() => {
    setState("loading");
    loadGameNews(TurfTalk.news, { gameId, league, limit: 5 })
      .then((r) => { setFeed(r); setState("ready"); })
      .catch((e) => { if (!(e instanceof AuthExpiredError)) { setFeed(null); setState("error"); } });
  }, [gameId, league]);

  useEffect(() => { load(); }, [load]);

  const status = feed?.status || "unavailable";
  const items = (feed?.items || []).slice(0, 5);
  const mode = feed?.mode || "none";
  const subnote = modeSubnote(mode, { awayAbbr, homeAbbr });

  return (
    <div className="tt-panel rounded-xl mt-4" data-testid="game-news-strip">
      <div className="px-4 h-11 flex items-center gap-2 border-b border-[#2A3544]">
        <Newspaper size={15} className="text-[#4ea152]" />
        <span className="font-display tracking-wide text-white text-sm" data-testid="game-news-mode">
          {mode === "none" ? "Game News" : modeLabel(mode, { awayAbbr, homeAbbr, league })}
        </span>
        {state === "ready" && status !== "unavailable" && items.length > 0 && (
          <span className={`text-[10px] px-1.5 py-px rounded border ${STATUS_STYLE[status]}`} data-testid="game-news-status">{statusLabel(status)}</span>
        )}
        <button type="button" onClick={load} aria-label="Refresh news" data-testid="game-news-refresh" className="ml-auto w-7 h-7 grid place-items-center rounded-md text-white/50 hover:text-white hover:bg-white/5 transition-colors">
          <RefreshCw size={13} />
        </button>
      </div>

      {state === "loading" && <div className="p-4 grid sm:grid-cols-2 gap-2"><Skeleton className="h-14" /><Skeleton className="h-14" /><Skeleton className="h-14" /><Skeleton className="h-14" /></div>}

      {state === "error" && (
        <div className="p-5 flex items-center justify-center gap-3 text-center" data-testid="game-news-error">
          <AlertCircle className="text-white/40" size={18} />
          <span className="text-white/60 text-sm">News is unavailable right now.</span>
          <button onClick={load} className="px-2.5 py-1 rounded-lg tt-panel-3 text-white text-xs inline-flex items-center gap-1.5" data-testid="game-news-error-retry"><RefreshCw size={12} /> Retry</button>
        </div>
      )}

      {state === "ready" && (status === "unavailable" || items.length === 0) && (
        <div className="p-5 text-center" data-testid="game-news-unavailable">
          <p className="text-white/55 text-sm">News is unavailable right now.</p>
        </div>
      )}

      {state === "ready" && items.length > 0 && (
        <>
          {subnote && <p className="px-4 pt-2 text-[11px] text-white/45" data-testid="game-news-subnote">{subnote}</p>}
          <div className="grid sm:grid-cols-2 gap-px" data-testid="game-news-list">
            {items.map((item) => {
              const st = (storyStatus && storyStatus[item.id]) || "idle";
              const isShared = st === "shared";
              const isPending = st === "pending";
              const isFailed = st === "failed";
              const label = isShared ? "In the room" : isPending ? "Sending…" : isFailed ? "Retry" : "Debate this";
              const Icon = isShared ? Check : isPending ? Loader2 : isFailed ? RotateCcw : MessageSquarePlus;
              return (
                <div key={item.id} className="px-4 py-3 flex flex-col gap-1.5" data-testid={`game-news-item-${item.id}`}>
                  {item.url ? (
                    <a href={item.url} target="_blank" rel="noopener noreferrer" className="text-sm text-white hover:text-[#7ec4e8] leading-snug inline-flex items-start gap-1 group" data-testid={`game-news-link-${item.id}`}>
                      <span className="break-words line-clamp-2">{item.title}</span>
                      <ExternalLink size={12} className="mt-0.5 shrink-0 text-white/30 group-hover:text-[#7ec4e8]" />
                    </a>
                  ) : <span className="text-sm text-white leading-snug break-words line-clamp-2">{item.title}</span>}
                  <div className="flex items-center gap-1.5 text-[11px] text-white/40" data-testid={`game-news-attrib-${item.id}`}>
                    <span className="truncate max-w-[55%]">{item.source}</span><span aria-hidden>·</span>
                    <span>{publishedLabel(item.publishedAt)}</span>
                  </div>
                  <button
                    type="button"
                    onClick={() => onDebate(item)}
                    disabled={isShared || isPending}
                    data-testid={`game-news-debate-${item.id}`}
                    data-share-state={st}
                    aria-label={isShared ? "In the room" : isFailed ? `Retry: ${item.title}` : `Debate this: ${item.title}`}
                    className={`self-start inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full text-[11px] font-medium border transition-colors disabled:cursor-not-allowed ${isFailed ? "border-[#D92D20] text-[#ff6b60] hover:bg-[#D92D20]/15" : "border-[#2F7D32] text-[#4ea152] hover:bg-[#2F7D32]/15"} disabled:opacity-40`}
                  >
                    <Icon size={12} className={isPending ? "animate-spin" : ""} /> {label}
                  </button>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
